import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  getToken, setToken, adminLogin, adminVerify,
  getAllPosts, adminCreatePost, adminUpdatePost, adminDeletePost,
  adminGetMessages, adminDeleteMessage, adminGetRegistrations, adminDeleteRegistration,
  adminGetSubscribers, adminDeleteSubscriber, adminGetLetterRequests, adminDeleteLetterRequest,
  adminGetPages, adminUpdatePage, adminChangePassword,
  adminGetProgramRegistrations, adminDeleteProgramRegistration, adminSendProgramNotice,
  adminGetIndividualRegistrations, adminDeleteIndividualRegistration, adminSendLeagueNotice,
  adminGetVolunteers, adminDeleteVolunteer, adminSendVolunteerNotice,
  getVolunteerSpotlight, adminSetVolunteerSpotlight, adminClearVolunteerSpotlight,
  getSpotlightArchive, adminDeleteSpotlightArchive,
  getResultsLinks, adminAddResultsLink, adminDeleteResultsLink,
  formatDate, CATEGORIES, clearCache,
} from '../lib/dataService';
import { LEAGUE_OPTIONS } from '../components/RegistrationForm';
import { VOLUNTEER_AREAS, VOLUNTEER_LEAGUES } from './VolunteerPage';
import { STATS_LEAGUES } from './ResultsPage';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { LogOut, Plus, Pencil, Trash2, ArrowLeft, Newspaper, Inbox, Search, Lock, ClipboardList, ChevronDown, Upload, Mail, FileText, Files, KeyRound, GraduationCap, Megaphone, Download, HeartHandshake, Star, BarChart3, ExternalLink } from 'lucide-react';
import RichTextEditor, { uploadFile } from '../components/RichTextEditor';
import './admin.css';

const EMPTY_FORM = { title: '', date: '', image: '', excerpt: '', content: '', categories: [] };

export default function AdminPage() {
  const [authed, setAuthed] = useState(false);
  const [checking, setChecking] = useState(true);
  const [creds, setCreds] = useState({ username: '', password: '' });
  const [tab, setTab] = useState('posts');
  const [posts, setPosts] = useState([]);
  const [messages, setMessages] = useState([]);
  const [registrations, setRegistrations] = useState([]);
  const [individualRegs, setIndividualRegs] = useState([]);
  const [regView, setRegView] = useState('teams');
  const [leagueFilter, setLeagueFilter] = useState('all');
  const [showLeagueNotice, setShowLeagueNotice] = useState(false);
  const [leagueNoticeForm, setLeagueNoticeForm] = useState({ league: '', audience: 'both', subject: '', message: '' });
  const [volunteers, setVolunteers] = useState([]);
  const [showVolNotice, setShowVolNotice] = useState(false);
  const [volNoticeForm, setVolNoticeForm] = useState({ subject: '', message: '', league: '', area: '' });
  const [showSpotlight, setShowSpotlight] = useState(false);
  const [spotlightForm, setSpotlightForm] = useState({ name: '', role: '', month: '', quote: '', photo: '' });
  const [spotlightActive, setSpotlightActive] = useState(false);
  const [spotlightArchive, setSpotlightArchive] = useState([]);
  const [resultsLinks, setResultsLinks] = useState([]);
  const [resultsForm, setResultsForm] = useState({ league: 'U13', division: 'Boys', season: '2026-27', label: '', url: '' });
  const [subscribers, setSubscribers] = useState([]);
  const [letterRequests, setLetterRequests] = useState([]);
  const [pages, setPages] = useState({});
  const [editingPage, setEditingPage] = useState(null); // null | {slug, title, content}
  const [pwForm, setPwForm] = useState({ current: '', next: '', confirm: '' });
  const [programRegs, setProgramRegs] = useState([]);
  const [programFilter, setProgramFilter] = useState('all');
  const [noticeForm, setNoticeForm] = useState({ program: 'kings', subject: '', message: '' });
  const [showNotice, setShowNotice] = useState(false);
  const [showAttendance, setShowAttendance] = useState(false);
  const [attForm, setAttForm] = useState({ program: 'academy', month: new Date().toISOString().slice(0, 7) });
  const [openReg, setOpenReg] = useState(null);
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(null); // null | 'new' | post object
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const handleFeaturedUpload = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const url = await uploadFile(file);
      setForm((f) => ({ ...f, image: url }));
      toast.success('Featured image uploaded');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    }
    setUploading(false);
    e.target.value = '';
  };

  const loadPosts = useCallback(async () => {
    clearCache();
    const data = await getAllPosts();
    setPosts(data);
  }, []);

  useEffect(() => {
    if (!getToken()) { setChecking(false); return; }
    adminVerify()
      .then(() => { setAuthed(true); loadPosts(); })
      .catch(() => setToken(null))
      .finally(() => setChecking(false));
  }, [loadPosts]);

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      await adminLogin(creds.username, creds.password);
      setAuthed(true);
      loadPosts();
      toast.success('Welcome back!');
    } catch (err) {
      toast.error('Invalid username or password');
    }
  };

  const handleLogout = () => {
    setToken(null);
    setAuthed(false);
    setCreds({ username: '', password: '' });
  };

  const openEdit = (post) => {
    setEditing(post);
    setForm({
      title: post.title,
      date: post.date,
      image: post.image || '',
      excerpt: post.excerpt || '',
      content: post.content || '',
      categories: post.categories || [],
    });
  };

  const openNew = () => {
    setEditing('new');
    setForm({ ...EMPTY_FORM, date: new Date().toISOString().slice(0, 10), categories: ['Latest news'] });
  };

  const toggleCat = (name) => {
    setForm((f) => ({
      ...f,
      categories: f.categories.includes(name) ? f.categories.filter((c) => c !== name) : [...f.categories, name],
    }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { toast.error('Title is required'); return; }
    setSaving(true);
    try {
      if (editing === 'new') {
        await adminCreatePost(form);
        toast.success('Post published!');
      } else {
        await adminUpdatePost(editing.id, form);
        toast.success('Post updated!');
      }
      setEditing(null);
      await loadPosts();
    } catch (err) {
      toast.error('Failed to save post');
    }
    setSaving(false);
  };

  const handleDelete = async (post) => {
    if (!window.confirm(`Delete "${post.title}"? This cannot be undone.`)) return;
    try {
      await adminDeletePost(post.id);
      toast.success('Post deleted');
      await loadPosts();
    } catch (err) {
      toast.error('Failed to delete post');
    }
  };

  const loadMessages = async () => {
    setTab('messages');
    try {
      setMessages(await adminGetMessages());
    } catch (err) {
      toast.error('Failed to load messages');
    }
  };

  const handleDeleteMsg = async (id) => {
    if (!window.confirm('Delete this message?')) return;
    await adminDeleteMessage(id);
    setMessages((m) => m.filter((x) => x.id !== id));
    toast.success('Message deleted');
  };

  const loadRegistrations = async () => {
    setTab('registrations');
    try {
      const [teams, individuals] = await Promise.all([adminGetRegistrations(), adminGetIndividualRegistrations()]);
      setRegistrations(teams);
      setIndividualRegs(individuals);
    } catch (err) {
      toast.error('Failed to load registrations');
    }
  };

  const handleDeleteIndividualReg = async (id) => {
    if (!window.confirm('Delete this individual player registration?')) return;
    await adminDeleteIndividualRegistration(id);
    setIndividualRegs((r) => r.filter((x) => x.id !== id));
    toast.success('Registration deleted');
  };

  const handleSendLeagueNotice = async (e) => {
    e.preventDefault();
    if (!leagueNoticeForm.league) { toast.error('Please select a league.'); return; }
    if (!leagueNoticeForm.subject.trim() || !leagueNoticeForm.message.trim()) {
      toast.error('Please enter a subject and message.'); return;
    }
    const audLabel = { teams: 'team officials', individuals: "individual players' parents", both: "team officials and individual players' parents" }[leagueNoticeForm.audience];
    if (!window.confirm(`Send this email to all ${audLabel} registered in the ${leagueNoticeForm.league}?`)) return;
    setSaving(true);
    try {
      const res = await adminSendLeagueNotice(leagueNoticeForm.league, leagueNoticeForm.audience, leagueNoticeForm.subject, leagueNoticeForm.message);
      toast.success(`Email queued for ${res.recipients} recipient(s)!`);
      setLeagueNoticeForm({ league: leagueNoticeForm.league, audience: leagueNoticeForm.audience, subject: '', message: '' });
      setShowLeagueNotice(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to send email');
    }
    setSaving(false);
  };

  const handleDeleteReg = async (id) => {
    if (!window.confirm('Delete this registration?')) return;
    await adminDeleteRegistration(id);
    setRegistrations((r) => r.filter((x) => x.id !== id));
    toast.success('Registration deleted');
  };

  const loadSubscribers = async () => {
    setTab('subscribers');
    try {
      setSubscribers(await adminGetSubscribers());
    } catch (err) {
      toast.error('Failed to load subscribers');
    }
  };

  const handleDeleteSub = async (id) => {
    if (!window.confirm('Remove this subscriber?')) return;
    await adminDeleteSubscriber(id);
    setSubscribers((s) => s.filter((x) => x.id !== id));
    toast.success('Subscriber removed');
  };

  const loadLetterRequests = async () => {
    setTab('letters');
    try {
      setLetterRequests(await adminGetLetterRequests());
    } catch (err) {
      toast.error('Failed to load letter requests');
    }
  };

  const handleDeleteLetter = async (id) => {
    if (!window.confirm('Delete this letter request?')) return;
    await adminDeleteLetterRequest(id);
    setLetterRequests((l) => l.filter((x) => x.id !== id));
    toast.success('Letter request deleted');
  };

  const loadPages = async () => {
    setTab('pages');
    setEditingPage(null);
    try {
      setPages(await adminGetPages());
    } catch (err) {
      toast.error('Failed to load pages');
    }
  };

  const handleSavePage = async (e) => {
    e.preventDefault();
    if (!editingPage.title.trim()) { toast.error('Page title is required'); return; }
    setSaving(true);
    try {
      await adminUpdatePage(editingPage.slug, { title: editingPage.title, content: editingPage.content });
      toast.success('Page updated!');
      setEditingPage(null);
      setPages(await adminGetPages());
    } catch (err) {
      toast.error('Failed to save page');
    }
    setSaving(false);
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (!pwForm.current || !pwForm.next) { toast.error('Please fill in all password fields.'); return; }
    if (pwForm.next.length < 8) { toast.error('New password must be at least 8 characters.'); return; }
    if (pwForm.next !== pwForm.confirm) { toast.error('New passwords do not match.'); return; }
    setSaving(true);
    try {
      await adminChangePassword(pwForm.current, pwForm.next);
      toast.success('Password changed! Use the new password next time you log in.');
      setPwForm({ current: '', next: '', confirm: '' });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to change password');
    }
    setSaving(false);
  };

  const loadVolunteers = async () => {
    setTab('volunteers');
    try {
      setVolunteers(await adminGetVolunteers());
      const s = await getVolunteerSpotlight();
      setSpotlightActive(!!s?.active);
      if (s?.active) setSpotlightForm({ name: s.name, role: s.role, month: s.month, quote: s.quote, photo: s.photo });
      setSpotlightArchive(await getSpotlightArchive());
    } catch (err) {
      toast.error('Failed to load volunteers');
    }
  };

  const handleSaveSpotlight = async (e) => {
    e.preventDefault();
    if (!spotlightForm.name.trim()) { toast.error("Please enter the volunteer's name."); return; }
    setSaving(true);
    try {
      await adminSetVolunteerSpotlight(spotlightForm);
      setSpotlightActive(true);
      setSpotlightArchive(await getSpotlightArchive());
      toast.success('Volunteer spotlight published on the homepage!');
      setShowSpotlight(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save spotlight');
    }
    setSaving(false);
  };

  const handleClearSpotlight = async () => {
    if (!window.confirm('Remove the volunteer spotlight from the homepage?')) return;
    try {
      await adminClearVolunteerSpotlight();
      setSpotlightActive(false);
      setSpotlightForm({ name: '', role: '', month: '', quote: '', photo: '' });
      toast.success('Spotlight removed from the homepage');
    } catch (err) {
      toast.error('Failed to remove spotlight');
    }
  };

  const handleDeleteArchiveEntry = async (id) => {
    if (!window.confirm('Remove this entry from the Wall of Fame on the volunteer page?')) return;
    try {
      await adminDeleteSpotlightArchive(id);
      setSpotlightArchive((a) => a.filter((x) => x.id !== id));
      toast.success('Wall of Fame entry removed');
    } catch (err) {
      toast.error('Failed to remove entry');
    }
  };

  const handleSpotlightPhotoUpload = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const url = await uploadFile(file);
      setSpotlightForm((f) => ({ ...f, photo: url }));
      toast.success('Photo uploaded');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    }
    setUploading(false);
    e.target.value = '';
  };

  const handleDeleteVolunteer = async (id) => {
    if (!window.confirm('Delete this volunteer sign-up?')) return;
    await adminDeleteVolunteer(id);
    setVolunteers((v) => v.filter((x) => x.id !== id));
    toast.success('Volunteer removed');
  };

  const handleSendVolunteerNotice = async (e) => {
    e.preventDefault();
    if (!volNoticeForm.subject.trim() || !volNoticeForm.message.trim()) {
      toast.error('Please enter a subject and message.'); return;
    }
    const target = [
      volNoticeForm.league || 'all leagues',
      volNoticeForm.area ? `${volNoticeForm.area} volunteers` : 'all roles',
    ].join(', ');
    if (!window.confirm(`Send this email to volunteers matching: ${target}?`)) return;
    setSaving(true);
    try {
      const res = await adminSendVolunteerNotice(volNoticeForm.subject, volNoticeForm.message, volNoticeForm.league, volNoticeForm.area);
      toast.success(`Email queued for ${res.recipients} volunteer(s)!`);
      setVolNoticeForm({ subject: '', message: '', league: volNoticeForm.league, area: volNoticeForm.area });
      setShowVolNotice(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to send email');
    }
    setSaving(false);
  };

  const exportVolunteers = () => {
    if (volunteers.length === 0) { toast.error('No volunteers to export.'); return; }
    const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const header = ['Name', 'Date of Birth', 'Email', 'Phone', 'Areas of Interest', 'Leagues', 'Experience', 'Signed Up On'];
    const body = volunteers.map((v) => [
      v.name, v.dob, v.email, v.phone,
      (v.areas || []).join('; '), (v.leagues || []).join('; '), v.experience || '',
      new Date(v.created_at).toLocaleString(),
    ].map(esc).join(','));
    const csv = [header.map(esc).join(','), ...body].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `volunteers-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${volunteers.length} volunteer(s) to CSV`);
  };

  const loadResultsLinks = async () => {
    setTab('results');
    try {
      setResultsLinks(await getResultsLinks());
    } catch (err) {
      toast.error('Failed to load results links');
    }
  };

  const handleAddResultsLink = async (e) => {
    e.preventDefault();
    if (!resultsForm.url.trim()) { toast.error('Please paste the results/stats link.'); return; }
    setSaving(true);
    try {
      await adminAddResultsLink(resultsForm);
      setResultsLinks(await getResultsLinks());
      setResultsForm({ ...resultsForm, label: '', url: '' });
      toast.success('Link added to the Results & Stats page!');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to add link');
    }
    setSaving(false);
  };

  const handleDeleteResultsLink = async (id) => {
    if (!window.confirm('Remove this link from the Results & Stats page?')) return;
    await adminDeleteResultsLink(id);
    setResultsLinks((l) => l.filter((x) => x.id !== id));
    toast.success('Link removed');
  };

  const loadProgramRegs = async (filter = programFilter) => {
    setTab('programs');
    try {
      setProgramRegs(await adminGetProgramRegistrations(filter));
    } catch (err) {
      toast.error('Failed to load program registrations');
    }
  };

  const changeProgramFilter = (f) => {
    setProgramFilter(f);
    loadProgramRegs(f);
  };

  const handleDeleteProgramReg = async (id) => {
    if (!window.confirm('Delete this program registration?')) return;
    await adminDeleteProgramRegistration(id);
    setProgramRegs((r) => r.filter((x) => x.id !== id));
    toast.success('Registration deleted');
  };

  const handleSendNotice = async (e) => {
    e.preventDefault();
    if (!noticeForm.subject.trim() || !noticeForm.message.trim()) {
      toast.error('Please enter a subject and message.'); return;
    }
    if (!window.confirm(`Send this notice to all registered ${noticeForm.program === 'all' ? 'program' : noticeForm.program === 'kings' ? 'Kings Development Program' : 'Basketball Academy'} parents?`)) return;
    setSaving(true);
    try {
      const res = await adminSendProgramNotice(noticeForm.program, noticeForm.subject, noticeForm.message);
      toast.success(`Notice queued for ${res.recipients} parent email(s)!`);
      setNoticeForm({ program: noticeForm.program, subject: '', message: '' });
      setShowNotice(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to send notice');
    }
    setSaving(false);
  };

  const exportIndividualRegs = () => {
    const rows = leagueFilter === 'all' ? individualRegs : individualRegs.filter((r) => r.league === leagueFilter);
    if (rows.length === 0) { toast.error('No individual registrations to export.'); return; }
    const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const header = ['League', 'Child Name', 'Date of Birth', 'School', 'District', 'Medical Conditions',
      'Parent 1 Name', 'Parent 1 Email', 'Parent 1 Phone',
      'Parent 2 Name', 'Parent 2 Email', 'Parent 2 Phone', 'Registered On'];
    const body = rows.map((r) => [
      r.league, r.child_name, r.child_dob, r.child_school, r.district || '', r.medical_conditions || '',
      r.parent1_name, r.parent1_email, r.parent1_phone,
      r.parent2_name || '', r.parent2_email || '', r.parent2_phone || '',
      new Date(r.created_at).toLocaleString(),
    ].map(esc).join(','));
    const csv = [header.map(esc).join(','), ...body].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const label = leagueFilter === 'all' ? 'all-leagues' : leagueFilter.toLowerCase().replace(/[^a-z0-9]+/g, '-');
    a.href = url;
    a.download = `${label}-individual-signups-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${rows.length} individual sign-up(s) to CSV`);
  };

  const exportTeamRegs = () => {
    const rows = leagueFilter === 'all' ? registrations : registrations.filter((r) => r.league === leagueFilter);
    if (rows.length === 0) { toast.error('No team registrations to export.'); return; }
    const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const header = ['League', 'Team Name', 'Primary Colour', 'Secondary Colour',
      'Coach Name', 'Coach Email', 'Coach Phone', 'Manager Name', 'Manager Email', 'Manager Phone',
      'Player #', 'Player Name', 'Date of Birth', 'Jersey #', 'Position', 'Nationality', 'Organization', 'Height', 'Weight', 'Registered On'];
    const body = [];
    rows.forEach((r) => {
      const team = [r.league, r.team_name, r.primary_colour || '', r.secondary_colour || '',
        r.coach_name, r.coach_email, r.coach_phone || '', r.manager_name, r.manager_email, r.manager_phone || ''];
      const named = (r.players || []).filter((p) => p.name);
      const when = new Date(r.created_at).toLocaleString();
      if (named.length === 0) { body.push([...team, '', '', '', '', '', '', '', '', '', when].map(esc).join(',')); return; }
      named.forEach((p, i) => {
        body.push([...team, i + 1, p.name, p.dob || '', p.jersey || '', p.position || '',
          p.nationality || '', p.organization || '', p.height || '', p.weight || '', when].map(esc).join(','));
      });
    });
    const csv = [header.map(esc).join(','), ...body].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const label = leagueFilter === 'all' ? 'all-leagues' : leagueFilter.toLowerCase().replace(/[^a-z0-9]+/g, '-');
    a.href = url;
    a.download = `${label}-team-registrations-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${rows.length} team(s) to CSV`);
  };

  const exportProgramRegs = () => {
    if (programRegs.length === 0) { toast.error('No registrations to export.'); return; }
    const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const header = ['Program', 'Child Name', 'Date of Birth', 'School', 'Medical Conditions',
      'Parent 1 Name', 'Parent 1 Email', 'Parent 1 Phone',
      'Parent 2 Name', 'Parent 2 Email', 'Parent 2 Phone',
      'Term(s)', 'Age Group', 'Jersey Size', 'Registered On'];
    const rows = programRegs.map((r) => [
      r.program === 'kings' ? 'Kings Development Program' : 'Basketball Academy',
      r.child_name, r.child_dob, r.child_school, r.medical_conditions || '',
      r.parent1_name, r.parent1_email, r.parent1_phone,
      r.parent2_name || '', r.parent2_email || '', r.parent2_phone || '',
      (r.terms || []).join('; '), r.age_group || '', r.jersey_size || '',
      new Date(r.created_at).toLocaleString(),
    ].map(esc).join(','));
    const csv = [header.map(esc).join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const label = programFilter === 'all' ? 'all-programs' : programFilter;
    a.href = url;
    a.download = `${label}-registrations-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${programRegs.length} registration(s) to CSV`);
  };

  const exportAttendance = async () => {
    if (!attForm.month) { toast.error('Please pick a month.'); return; }
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/program-registrations/attendance?program=${attForm.program}&month=${attForm.month}`,
        { headers: { Authorization: `Bearer ${getToken()}` }, responseType: 'blob' }
      );
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${attForm.program}-attendance-${attForm.month}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Attendance sheet exported (formatted Excel file)');
    } catch (err) {
      if (err?.response?.status === 404) toast.error('No registrations found for this program.');
      else if (err?.response?.status === 422) toast.error('No session dates in that month.');
      else toast.error('Failed to export attendance sheet');
    }
  };

  if (checking) return <div className="container section"><div className="loading-block" /></div>;

  /* ---------- LOGIN ---------- */
  if (!authed) {
    return (
      <div className="admin-login-wrap">
        <form className="admin-login-card" onSubmit={handleLogin}>
          <div className="quick-icon" style={{ margin: '0 auto 14px' }}><Lock size={24} /></div>
          <h1>Admin Login</h1>
          <p>Sign in to manage news posts and messages.</p>
          <Input placeholder="Username" value={creds.username} onChange={(e) => setCreds({ ...creds, username: e.target.value })} autoFocus />
          <Input type="password" placeholder="Password" value={creds.password} onChange={(e) => setCreds({ ...creds, password: e.target.value })} />
          <Button type="submit" className="btn-submit">Sign In</Button>
          <Link to="/" className="admin-back-home"><ArrowLeft size={14} /> Back to website</Link>
        </form>
      </div>
    );
  }

  /* ---------- EDITOR ---------- */
  if (editing) {
    return (
      <div className="container section admin-wrap">
        <button className="admin-back" onClick={() => setEditing(null)}><ArrowLeft size={16} /> Back to posts</button>
        <h1 className="admin-title">{editing === 'new' ? 'New Post' : 'Edit Post'}</h1>
        <form className="admin-form" onSubmit={handleSave}>
          <label>Title *</label>
          <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Post title" />
          <div className="admin-form-row">
            <div>
              <label>Date</label>
              <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
            </div>
            <div>
              <label>Featured image</label>
              <div className="admin-img-row">
                <Input value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} placeholder="Paste URL or upload a file" />
                <label className="admin-upload-btn">
                  <Upload size={15} /> {uploading ? 'Uploading...' : 'Upload'}
                  <input type="file" accept="image/*" onChange={handleFeaturedUpload} hidden />
                </label>
              </div>
            </div>
          </div>
          {form.image && <img src={form.image} alt="preview" className="admin-img-preview" />}
          <label>Categories</label>
          <div className="cat-filters">
            {CATEGORIES.map((c) => (
              <button type="button" key={c.slug} className={`cat-chip ${form.categories.includes(c.name) ? 'active' : ''}`} onClick={() => toggleCat(c.name)}>
                {c.name}
              </button>
            ))}
          </div>
          <label>Excerpt (short summary shown on cards)</label>
          <Textarea rows={2} value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} placeholder="Optional — auto-generated from content if left empty" />
          <label>Content — format text and insert photos with the toolbar below</label>
          <RichTextEditor value={form.content} onChange={(html) => setForm((f) => ({ ...f, content: html }))} />
          <div className="admin-form-actions">
            <Button type="submit" disabled={saving} className="btn-submit">{saving ? 'Saving...' : editing === 'new' ? 'Publish Post' : 'Save Changes'}</Button>
            <Button type="button" variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
          </div>
        </form>
        {form.content && (
          <div className="admin-preview">
            <h3>Live Preview</h3>
            <div className="wp-content" dangerouslySetInnerHTML={{ __html: form.content }} />
          </div>
        )}
      </div>
    );
  }

  /* ---------- DASHBOARD ---------- */
  const filtered = posts.filter((p) => p.title.toLowerCase().includes(search.toLowerCase()));
  const leaguesInData = [...new Set([...registrations.map((r) => r.league), ...individualRegs.map((r) => r.league)])].filter(Boolean).sort();
  const filteredTeamRegs = leagueFilter === 'all' ? registrations : registrations.filter((r) => r.league === leagueFilter);
  const filteredIndividualRegs = leagueFilter === 'all' ? individualRegs : individualRegs.filter((r) => r.league === leagueFilter);

  return (
    <div className="container section admin-wrap">
      <div className="admin-header">
        <div>
          <h1 className="admin-title">Admin Dashboard</h1>
          <p className="admin-sub">{posts.length} posts published</p>
        </div>
        <div className="admin-header-actions">
          <Link to="/" className="admin-back"><ArrowLeft size={15} /> View Site</Link>
          <Button variant="outline" onClick={handleLogout}><LogOut size={15} /> Logout</Button>
        </div>
      </div>

      <div className="admin-tabs">
        <button className={`admin-tab ${tab === 'posts' ? 'active' : ''}`} onClick={() => setTab('posts')}><Newspaper size={16} /> Posts</button>
        <button className={`admin-tab ${tab === 'pages' ? 'active' : ''}`} onClick={loadPages}><Files size={16} /> Pages</button>
        <button className={`admin-tab ${tab === 'messages' ? 'active' : ''}`} onClick={loadMessages}><Inbox size={16} /> Messages</button>
        <button className={`admin-tab ${tab === 'registrations' ? 'active' : ''}`} onClick={loadRegistrations}><ClipboardList size={16} /> Registrations</button>
        <button className={`admin-tab ${tab === 'programs' ? 'active' : ''}`} onClick={() => loadProgramRegs()}><GraduationCap size={16} /> Programs</button>
        <button className={`admin-tab ${tab === 'subscribers' ? 'active' : ''}`} onClick={loadSubscribers}><Mail size={16} /> Subscribers</button>
        <button className={`admin-tab ${tab === 'volunteers' ? 'active' : ''}`} onClick={loadVolunteers} data-testid="admin-tab-volunteers"><HeartHandshake size={16} /> Volunteers</button>
        <button className={`admin-tab ${tab === 'results' ? 'active' : ''}`} onClick={loadResultsLinks} data-testid="admin-tab-results"><BarChart3 size={16} /> Results</button>
        <button className={`admin-tab ${tab === 'letters' ? 'active' : ''}`} onClick={loadLetterRequests}><FileText size={16} /> Letters</button>
        <button className={`admin-tab ${tab === 'settings' ? 'active' : ''}`} onClick={() => setTab('settings')}><KeyRound size={16} /> Settings</button>
      </div>

      {tab === 'posts' && (
        <>
          <div className="admin-toolbar">
            <div className="news-search">
              <Search size={16} className="news-search-icon" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search posts..." className="news-search-input" />
            </div>
            <Button onClick={openNew} className="btn-submit"><Plus size={16} /> New Post</Button>
          </div>
          <div className="admin-post-list">
            {filtered.map((p) => (
              <div key={p.id} className="admin-post-row">
                <img src={p.image || '/media/uploads/2023/12/5d3a86e0-8698-43d8-8d99-20bfb54dbcc8.webp'} alt="" className="admin-post-thumb" loading="lazy" />
                <div className="admin-post-info">
                  <span className="admin-post-title">{p.title}</span>
                  <span className="admin-post-meta">{formatDate(p.date)} · {(p.categories || []).join(', ') || 'Uncategorized'}</span>
                </div>
                <div className="admin-post-actions">
                  <Link to={`/news/${p.slug}`} className="admin-icon-btn" title="View"><Newspaper size={16} /></Link>
                  <button className="admin-icon-btn" title="Edit" onClick={() => openEdit(p)}><Pencil size={16} /></button>
                  <button className="admin-icon-btn danger" title="Delete" onClick={() => handleDelete(p)}><Trash2 size={16} /></button>
                </div>
              </div>
            ))}
            {filtered.length === 0 && <p className="empty-state">No posts found.</p>}
          </div>
        </>
      )}

      {tab === 'pages' && (
        editingPage ? (
          <div>
            <button className="admin-back" onClick={() => setEditingPage(null)}><ArrowLeft size={16} /> Back to pages</button>
            <h2 className="admin-title" style={{ fontSize: 26, margin: '14px 0 0' }}>Edit Page</h2>
            <form className="admin-form" onSubmit={handleSavePage}>
              <label>Page Title</label>
              <Input value={editingPage.title} onChange={(e) => setEditingPage({ ...editingPage, title: e.target.value })} />
              {/(<table)/i.test(editingPage.content) ? (
                <>
                  <div className="admin-table-warning">
                    This page contains a <strong>table</strong>. To protect the table layout, it is edited as HTML here —
                    the visual editor would scramble the columns. Edit the text between the tags and click Save.
                    Each table row must look like: <code>&lt;tr&gt;&lt;td&gt;Date&lt;/td&gt;&lt;td&gt;Description&lt;/td&gt;&lt;/tr&gt;</code>
                  </div>
                  <label>Content (HTML)</label>
                  <Textarea
                    rows={22}
                    className="admin-content-area"
                    value={editingPage.content}
                    onChange={(e) => setEditingPage((p) => ({ ...p, content: e.target.value }))}
                  />
                  <div className="admin-preview">
                    <h3>Live Preview</h3>
                    <div className="wp-content" dangerouslySetInnerHTML={{ __html: editingPage.content }} />
                  </div>
                </>
              ) : (
                <>
                  <label>Content — format text and insert photos with the toolbar below</label>
                  <RichTextEditor value={editingPage.content} onChange={(html) => setEditingPage((p) => ({ ...p, content: html }))} />
                </>
              )}
              <div className="admin-form-actions">
                <Button type="submit" disabled={saving} className="btn-submit">{saving ? 'Saving...' : 'Save Page'}</Button>
                <Button type="button" variant="outline" onClick={() => setEditingPage(null)}>Cancel</Button>
              </div>
            </form>
          </div>
        ) : (
          <div className="admin-post-list">
            <p className="admin-sub">Edit the content of any page on the website. Registration forms on league pages are managed automatically and stay in place.</p>
            {Object.entries(pages)
              .sort((a, b) => a[1].title.localeCompare(b[1].title))
              .map(([slug, pg]) => (
                <div key={slug} className="admin-post-row">
                  <div className="admin-post-info">
                    <span className="admin-post-title">{pg.title}</span>
                    <span className="admin-post-meta">/page/{slug}</span>
                  </div>
                  <div className="admin-post-actions">
                    <Link to={`/page/${slug}`} className="admin-icon-btn" title="View"><Newspaper size={16} /></Link>
                    <button className="admin-icon-btn" title="Edit" onClick={() => setEditingPage({ slug, title: pg.title, content: pg.content })}><Pencil size={16} /></button>
                  </div>
                </div>
              ))}
          </div>
        )
      )}

      {tab === 'settings' && (
        <div className="admin-settings">
          <h3 className="admin-settings-title"><KeyRound size={18} /> Change Admin Password</h3>
          <p className="admin-sub">After changing, use the new password the next time you log in. Minimum 8 characters.</p>
          <form className="admin-form admin-pw-form" onSubmit={handleChangePassword}>
            <label>Current Password</label>
            <Input type="password" value={pwForm.current} onChange={(e) => setPwForm({ ...pwForm, current: e.target.value })} autoComplete="current-password" />
            <label>New Password</label>
            <Input type="password" value={pwForm.next} onChange={(e) => setPwForm({ ...pwForm, next: e.target.value })} autoComplete="new-password" />
            <label>Confirm New Password</label>
            <Input type="password" value={pwForm.confirm} onChange={(e) => setPwForm({ ...pwForm, confirm: e.target.value })} autoComplete="new-password" />
            <div className="admin-form-actions">
              <Button type="submit" disabled={saving} className="btn-submit">{saving ? 'Saving...' : 'Change Password'}</Button>
            </div>
          </form>
        </div>
      )}

      {tab === 'registrations' && (
        <div className="admin-post-list">
          <div className="admin-toolbar">
            <div className="cat-filters">
              <button className={`cat-chip ${regView === 'teams' ? 'active' : ''}`} onClick={() => setRegView('teams')} data-testid="admin-reg-view-teams">Teams ({filteredTeamRegs.length})</button>
              <button className={`cat-chip ${regView === 'individuals' ? 'active' : ''}`} onClick={() => setRegView('individuals')} data-testid="admin-reg-view-individuals">Individual Players ({filteredIndividualRegs.length})</button>
            </div>
            <div className="admin-header-actions">
              <select
                className="admin-league-select"
                style={{ maxWidth: 230 }}
                value={leagueFilter}
                onChange={(e) => setLeagueFilter(e.target.value)}
                data-testid="admin-league-filter"
              >
                <option value="all">All leagues</option>
                {leaguesInData.map((l) => <option key={l} value={l}>{l}</option>)}
              </select>
              {regView === 'individuals' ? (
                <Button variant="outline" onClick={exportIndividualRegs} data-testid="individual-export-csv-btn"><Download size={15} /> Export CSV</Button>
              ) : (
                <Button variant="outline" onClick={exportTeamRegs} data-testid="team-export-csv-btn"><Download size={15} /> Export CSV</Button>
              )}
              <Button onClick={() => setShowLeagueNotice(!showLeagueNotice)} className="btn-submit" data-testid="admin-league-notice-btn"><Megaphone size={15} /> Email a League</Button>
            </div>
          </div>

          {showLeagueNotice && (
            <form className="admin-form admin-notice-form" onSubmit={handleSendLeagueNotice}>
              <h3 className="admin-settings-title"><Megaphone size={17} /> Email League Registrations</h3>
              <p className="admin-sub">Sends an email to everyone registered in the selected league — team officials (coaches &amp; managers), the parents of individually signed-up players, or both.</p>
              <label>League</label>
              <select
                className="admin-league-select"
                value={leagueNoticeForm.league}
                onChange={(e) => setLeagueNoticeForm({ ...leagueNoticeForm, league: e.target.value })}
                data-testid="league-notice-league-select"
              >
                <option value="">Select a league</option>
                {LEAGUE_OPTIONS.map((l) => <option key={l} value={l}>{l}</option>)}
              </select>
              <label>Send To</label>
              <div className="cat-filters">
                {[['teams', 'Team officials'], ['individuals', "Individual players (parents)"], ['both', 'Both']].map(([v, l]) => (
                  <button type="button" key={v} className={`cat-chip ${leagueNoticeForm.audience === v ? 'active' : ''}`} onClick={() => setLeagueNoticeForm({ ...leagueNoticeForm, audience: v })} data-testid={`league-notice-audience-${v}`}>{l}</button>
                ))}
              </div>
              <label>Subject</label>
              <Input value={leagueNoticeForm.subject} onChange={(e) => setLeagueNoticeForm({ ...leagueNoticeForm, subject: e.target.value })} placeholder="E.g. Game schedule for opening weekend" data-testid="league-notice-subject" />
              <label>Message</label>
              <Textarea rows={5} value={leagueNoticeForm.message} onChange={(e) => setLeagueNoticeForm({ ...leagueNoticeForm, message: e.target.value })} placeholder="Write your message to the league here..." data-testid="league-notice-message" />
              <div className="admin-form-actions">
                <Button type="submit" disabled={saving} className="btn-submit" data-testid="league-notice-send-btn">{saving ? 'Sending...' : 'Send Email'}</Button>
                <Button type="button" variant="outline" onClick={() => setShowLeagueNotice(false)}>Cancel</Button>
              </div>
            </form>
          )}

          {regView === 'individuals' ? (
            <>
              {filteredIndividualRegs.length === 0 && <p className="empty-state">No individual player registrations{leagueFilter !== 'all' ? ` for the ${leagueFilter}` : ''} yet.</p>}
              {filteredIndividualRegs.map((r) => (
                <div key={r.id} className="admin-msg-card" data-testid="individual-reg-card">
                  <div className="admin-msg-head">
                    <div>
                      <strong>{r.child_name}</strong>
                      <span className="reg-status ok">{r.league}</span>
                    </div>
                    <div className="admin-msg-right">
                      <span className="admin-post-meta">{new Date(r.created_at).toLocaleString()}</span>
                      <button className="admin-icon-btn danger" onClick={() => handleDeleteIndividualReg(r.id)} data-testid="individual-reg-delete-btn"><Trash2 size={15} /></button>
                    </div>
                  </div>
                  <p><strong>DOB:</strong> {r.child_dob} · <strong>School:</strong> {r.child_school}{r.district ? <> · <strong>District:</strong> {r.district}</> : null}</p>
                  {r.medical_conditions && r.medical_conditions.trim() && (
                    <p className="reg-medical"><strong>Medical conditions (confidential):</strong> {r.medical_conditions}</p>
                  )}
                  <p><strong>Parent 1:</strong> {r.parent1_name} — <a href={`mailto:${r.parent1_email}`}>{r.parent1_email}</a> · {r.parent1_phone}</p>
                  {r.parent2_name && (
                    <p><strong>Parent 2:</strong> {r.parent2_name}{r.parent2_email ? <> — <a href={`mailto:${r.parent2_email}`}>{r.parent2_email}</a></> : ''}{r.parent2_phone ? ` · ${r.parent2_phone}` : ''}</p>
                  )}
                </div>
              ))}
            </>
          ) : (
            <>
              {filteredTeamRegs.length === 0 && <p className="empty-state">No team registrations{leagueFilter !== 'all' ? ` for the ${leagueFilter}` : ''} yet.</p>}
          {filteredTeamRegs.map((r) => (
            <div key={r.id} className="admin-msg-card">
              <div className="admin-msg-head">
                <div>
                  <strong>{r.team_name}</strong>
                  <span className="admin-msg-subject"> — {r.league}</span>
                  <span className={`reg-status ${r.email_status === 'sent' ? 'ok' : r.email_status === 'failed' ? 'bad' : ''}`}>
                    Email: {r.email_status || 'pending'}
                  </span>
                </div>
                <div className="admin-msg-right">
                  <span className="admin-post-meta">{new Date(r.created_at).toLocaleString()}</span>
                  <button className="admin-icon-btn" onClick={() => setOpenReg(openReg === r.id ? null : r.id)} title="Details">
                    <ChevronDown size={15} style={{ transform: openReg === r.id ? 'rotate(180deg)' : 'none' }} />
                  </button>
                  <button className="admin-icon-btn danger" onClick={() => handleDeleteReg(r.id)}><Trash2 size={15} /></button>
                </div>
              </div>
              <p>
                Coach: {r.coach_name} ({r.coach_email}{r.coach_phone ? `, ${r.coach_phone}` : ''}) · Manager: {r.manager_name} ({r.manager_email}{r.manager_phone ? `, ${r.manager_phone}` : ''})
              </p>
              {openReg === r.id && (
                <div className="reg-detail">
                  <p><strong>Colours:</strong> {r.primary_colour || '-'} / {r.secondary_colour || '-'}
                    {r.assistant_name && <> · <strong>Assistant:</strong> {r.assistant_name} ({r.assistant_email})</>}</p>
                  <table className="reg-detail-table">
                    <thead>
                      {(r.league || '').toLowerCase().includes('corporate') ? (
                        <tr><th>#</th><th>Name</th><th>DOB</th><th>Jersey</th><th>Pos</th><th>Organization</th></tr>
                      ) : (
                        <tr><th>#</th><th>Name</th><th>DOB</th><th>Jersey</th><th>Pos</th><th>Nationality</th><th>Height</th><th>Weight</th></tr>
                      )}
                    </thead>
                    <tbody>
                      {(r.players || []).filter((p) => p.name).map((p, i) => (
                        (r.league || '').toLowerCase().includes('corporate') ? (
                          <tr key={i}><td>{i + 1}</td><td>{p.name}</td><td>{p.dob}</td><td>{p.jersey}</td><td>{p.position}</td><td>{p.organization}</td></tr>
                        ) : (
                          <tr key={i}><td>{i + 1}</td><td>{p.name}</td><td>{p.dob}</td><td>{p.jersey}</td><td>{p.position}</td><td>{p.nationality}</td><td>{p.height}</td><td>{p.weight}</td></tr>
                        )
                      ))}
                    </tbody>
                  </table>
                  {r.email_status === 'failed' && <p className="reg-email-err">Email delivery issue: {r.email_detail}</p>}
                </div>
              )}
            </div>
          ))}
            </>
          )}
        </div>
      )}

      {tab === 'programs' && (
        <div className="admin-post-list">
          <div className="admin-toolbar">
            <div className="cat-filters">
              <button className={`cat-chip ${programFilter === 'all' ? 'active' : ''}`} onClick={() => changeProgramFilter('all')}>All Programs</button>
              <button className={`cat-chip ${programFilter === 'kings' ? 'active' : ''}`} onClick={() => changeProgramFilter('kings')}>Kings Development Program</button>
              <button className={`cat-chip ${programFilter === 'academy' ? 'active' : ''}`} onClick={() => changeProgramFilter('academy')}>Basketball Academy</button>
            </div>
            <div className="admin-header-actions">
              <Button variant="outline" onClick={() => setShowAttendance(!showAttendance)}><ClipboardList size={15} /> Attendance Sheet</Button>
              <Button variant="outline" onClick={exportProgramRegs}><Download size={15} /> Export CSV</Button>
              <Button onClick={() => setShowNotice(!showNotice)} className="btn-submit"><Megaphone size={15} /> Send Notice</Button>
            </div>
          </div>

          {showAttendance && (
            <div className="admin-form admin-notice-form">
              <h3 className="admin-settings-title"><ClipboardList size={17} /> Export Attendance Roster</h3>
              <p className="admin-sub">Generates a printable sheet with each participant, their age group program, and a column for every session date in the chosen month (Academy: Saturdays · Kings: Thursdays &amp; Fridays). Open in Excel and print for the venue.</p>
              <label>Program</label>
              <div className="cat-filters">
                {[['academy', 'Basketball Academy'], ['kings', 'Kings Development Program']].map(([v, l]) => (
                  <button type="button" key={v} className={`cat-chip ${attForm.program === v ? 'active' : ''}`} onClick={() => setAttForm({ ...attForm, program: v })}>{l}</button>
                ))}
              </div>
              <label>Month</label>
              <Input type="month" value={attForm.month} onChange={(e) => setAttForm({ ...attForm, month: e.target.value })} style={{ maxWidth: 220 }} />
              <div className="admin-form-actions">
                <Button type="button" onClick={exportAttendance} className="btn-submit"><Download size={15} /> Export Attendance Sheet</Button>
                <Button type="button" variant="outline" onClick={() => setShowAttendance(false)}>Close</Button>
              </div>
            </div>
          )}

          {showNotice && (
            <form className="admin-form admin-notice-form" onSubmit={handleSendNotice}>
              <h3 className="admin-settings-title"><Megaphone size={17} /> Email Notice to Parents</h3>
              <p className="admin-sub">Sends an email to all registered parents of the selected program. Payment reminders are sent automatically on the first Tuesday of each month — use this for other important notices.</p>
              <label>Send To</label>
              <div className="cat-filters">
                {[['kings', 'Kings Program parents'], ['academy', 'Academy parents'], ['all', 'All program parents']].map(([v, l]) => (
                  <button type="button" key={v} className={`cat-chip ${noticeForm.program === v ? 'active' : ''}`} onClick={() => setNoticeForm({ ...noticeForm, program: v })}>{l}</button>
                ))}
              </div>
              <label>Subject</label>
              <Input value={noticeForm.subject} onChange={(e) => setNoticeForm({ ...noticeForm, subject: e.target.value })} placeholder="E.g. No session this Friday" />
              <label>Message</label>
              <Textarea rows={5} value={noticeForm.message} onChange={(e) => setNoticeForm({ ...noticeForm, message: e.target.value })} placeholder="Write your notice to parents here..." />
              <div className="admin-form-actions">
                <Button type="submit" disabled={saving} className="btn-submit">{saving ? 'Sending...' : 'Send Notice'}</Button>
                <Button type="button" variant="outline" onClick={() => setShowNotice(false)}>Cancel</Button>
              </div>
            </form>
          )}

          <p className="admin-sub">{programRegs.length} registration{programRegs.length === 1 ? '' : 's'}</p>
          {programRegs.length === 0 && <p className="empty-state">No program registrations yet.</p>}
          {programRegs.map((r) => (
            <div key={r.id} className="admin-msg-card">
              <div className="admin-msg-head">
                <div>
                  <strong>{r.child_name}</strong>
                  <span className={`reg-status ${r.program === 'kings' ? 'ok' : ''}`}>{r.program === 'kings' ? 'Kings Program' : 'Academy'}</span>
                </div>
                <div className="admin-msg-right">
                  <span className="admin-post-meta">{new Date(r.created_at).toLocaleString()}</span>
                  <button className="admin-icon-btn danger" onClick={() => handleDeleteProgramReg(r.id)}><Trash2 size={15} /></button>
                </div>
              </div>
              <p><strong>DOB:</strong> {r.child_dob} · <strong>School:</strong> {r.child_school}</p>
              {r.program === 'academy' && (
                <p>
                  {(r.terms || []).length > 0 && <><strong>Term(s):</strong> {(r.terms || []).join(', ')} · </>}
                  {r.age_group && <><strong>Group:</strong> {r.age_group} · </>}
                  {r.jersey_size && <><strong>Jersey:</strong> {r.jersey_size}</>}
                </p>
              )}
              {r.medical_conditions && r.medical_conditions.trim() && (
                <p className="reg-medical"><strong>Medical conditions (confidential):</strong> {r.medical_conditions}</p>
              )}
              <p><strong>Parent 1:</strong> {r.parent1_name} — <a href={`mailto:${r.parent1_email}`}>{r.parent1_email}</a> · {r.parent1_phone}</p>
              {r.parent2_name && (
                <p><strong>Parent 2:</strong> {r.parent2_name}{r.parent2_email ? <> — <a href={`mailto:${r.parent2_email}`}>{r.parent2_email}</a></> : ''}{r.parent2_phone ? ` · ${r.parent2_phone}` : ''}</p>
              )}
            </div>
          ))}
        </div>
      )}

      {tab === 'subscribers' && (
        <div className="admin-post-list">
          <p className="admin-sub">{subscribers.length} newsletter subscriber{subscribers.length === 1 ? '' : 's'} — they receive an email whenever a new story is published.</p>
          {subscribers.length === 0 && <p className="empty-state">No subscribers yet.</p>}
          {subscribers.map((s) => (
            <div key={s.id} className="admin-post-row">
              <div className="admin-post-info">
                <span className="admin-post-title">{s.email}</span>
                <span className="admin-post-meta">{s.name ? `${s.name} · ` : ''}Joined {new Date(s.created_at).toLocaleDateString()}</span>
              </div>
              <div className="admin-post-actions">
                <button className="admin-icon-btn danger" title="Remove" onClick={() => handleDeleteSub(s.id)}><Trash2 size={16} /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === 'volunteers' && (
        <div className="admin-post-list">
          <div className="admin-toolbar">
            <p className="admin-sub" style={{ margin: 0 }}>{volunteers.length} volunteer sign-up{volunteers.length === 1 ? '' : 's'}</p>
            <div className="admin-header-actions">
              <Button variant="outline" onClick={() => setShowSpotlight(!showSpotlight)} data-testid="admin-spotlight-btn"><Star size={15} /> Spotlight {spotlightActive ? '(live)' : ''}</Button>
              <Button variant="outline" onClick={exportVolunteers} data-testid="volunteer-export-csv-btn"><Download size={15} /> Export CSV</Button>
              <Button onClick={() => setShowVolNotice(!showVolNotice)} className="btn-submit" data-testid="admin-volunteer-notice-btn"><Megaphone size={15} /> Email Volunteers</Button>
            </div>
          </div>

          {showSpotlight && (
            <form className="admin-form admin-notice-form" onSubmit={handleSaveSpotlight}>
              <h3 className="admin-settings-title"><Star size={17} /> Volunteer of the Month — Homepage Spotlight</h3>
              <p className="admin-sub">Featured on the homepage with a "Become a Volunteer" button to encourage sign-ups. {spotlightActive ? 'A spotlight is currently LIVE on the homepage.' : 'No spotlight is currently shown.'}</p>
              <div className="admin-form-row">
                <div>
                  <label>Volunteer Name *</label>
                  <Input value={spotlightForm.name} onChange={(e) => setSpotlightForm({ ...spotlightForm, name: e.target.value })} placeholder="E.g. Jane Smith" data-testid="spotlight-form-name" />
                </div>
                <div>
                  <label>Role</label>
                  <Input value={spotlightForm.role} onChange={(e) => setSpotlightForm({ ...spotlightForm, role: e.target.value })} placeholder="E.g. Referee — 13 & Under Leagues" data-testid="spotlight-form-role" />
                </div>
              </div>
              <div className="admin-form-row">
                <div>
                  <label>Month</label>
                  <Input value={spotlightForm.month} onChange={(e) => setSpotlightForm({ ...spotlightForm, month: e.target.value })} placeholder={`E.g. ${new Date().toLocaleString('en', { month: 'long', year: 'numeric' })}`} data-testid="spotlight-form-month" />
                </div>
                <div>
                  <label>Photo</label>
                  <div className="admin-img-row">
                    <Input value={spotlightForm.photo} onChange={(e) => setSpotlightForm({ ...spotlightForm, photo: e.target.value })} placeholder="Paste URL or upload" />
                    <label className="admin-upload-btn">
                      <Upload size={15} /> {uploading ? 'Uploading...' : 'Upload'}
                      <input type="file" accept="image/*" onChange={handleSpotlightPhotoUpload} hidden />
                    </label>
                  </div>
                </div>
              </div>
              {spotlightForm.photo && <img src={spotlightForm.photo} alt="preview" className="admin-img-preview" style={{ maxWidth: 140, borderRadius: '50%' }} />}
              <label>Quote / Why they volunteer</label>
              <Textarea rows={3} value={spotlightForm.quote} onChange={(e) => setSpotlightForm({ ...spotlightForm, quote: e.target.value })} placeholder="A short quote or note about this volunteer..." data-testid="spotlight-form-quote" />
              <div className="admin-form-actions">
                <Button type="submit" disabled={saving} className="btn-submit" data-testid="spotlight-save-btn">{saving ? 'Saving...' : 'Publish to Homepage'}</Button>
                {spotlightActive && <Button type="button" variant="outline" onClick={handleClearSpotlight} data-testid="spotlight-clear-btn"><Trash2 size={14} /> Remove from Homepage</Button>}
                <Button type="button" variant="outline" onClick={() => setShowSpotlight(false)}>Close</Button>
              </div>
              {spotlightArchive.length > 0 && (
                <div style={{ marginTop: 22 }}>
                  <label>Wall of Fame — shown on the Volunteer page ({spotlightArchive.length} entr{spotlightArchive.length === 1 ? 'y' : 'ies'})</label>
                  {spotlightArchive.map((a) => (
                    <div key={a.id} className="admin-post-row" data-testid="archive-entry-row">
                      <div className="admin-post-info">
                        <span className="admin-post-title">{a.name}</span>
                        <span className="admin-post-meta">{[a.month, a.role].filter(Boolean).join(' · ')}</span>
                      </div>
                      <div className="admin-post-actions">
                        <button type="button" className="admin-icon-btn danger" title="Remove from Wall of Fame" onClick={() => handleDeleteArchiveEntry(a.id)} data-testid="archive-entry-delete-btn"><Trash2 size={15} /></button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </form>
          )}

          {showVolNotice && (
            <form className="admin-form admin-notice-form" onSubmit={handleSendVolunteerNotice}>
              <h3 className="admin-settings-title"><Megaphone size={17} /> Broadcast Email to Volunteers</h3>
              <p className="admin-sub">Send to every volunteer, or narrow it down to a specific league and/or role.</p>
              <div className="admin-form-row">
                <div>
                  <label>League</label>
                  <select
                    className="admin-league-select"
                    value={volNoticeForm.league}
                    onChange={(e) => setVolNoticeForm({ ...volNoticeForm, league: e.target.value })}
                    data-testid="volunteer-notice-league-select"
                  >
                    <option value="">All leagues</option>
                    {VOLUNTEER_LEAGUES.map((l) => <option key={l.name} value={l.name}>{l.name}</option>)}
                  </select>
                </div>
                <div>
                  <label>Role</label>
                  <select
                    className="admin-league-select"
                    value={volNoticeForm.area}
                    onChange={(e) => setVolNoticeForm({ ...volNoticeForm, area: e.target.value })}
                    data-testid="volunteer-notice-area-select"
                  >
                    <option value="">All roles</option>
                    {VOLUNTEER_AREAS.map((a) => <option key={a} value={a}>{a}</option>)}
                  </select>
                </div>
              </div>
              <label>Subject</label>
              <Input value={volNoticeForm.subject} onChange={(e) => setVolNoticeForm({ ...volNoticeForm, subject: e.target.value })} placeholder="E.g. Table official training this Saturday" data-testid="volunteer-notice-subject" />
              <label>Message</label>
              <Textarea rows={5} value={volNoticeForm.message} onChange={(e) => setVolNoticeForm({ ...volNoticeForm, message: e.target.value })} placeholder="Write your message to volunteers here..." data-testid="volunteer-notice-message" />
              <div className="admin-form-actions">
                <Button type="submit" disabled={saving} className="btn-submit" data-testid="volunteer-notice-send-btn">{saving ? 'Sending...' : 'Send Email'}</Button>
                <Button type="button" variant="outline" onClick={() => setShowVolNotice(false)}>Cancel</Button>
              </div>
            </form>
          )}

          {volunteers.length === 0 && <p className="empty-state">No volunteer sign-ups yet.</p>}
          {volunteers.map((v) => (
            <div key={v.id} className="admin-msg-card" data-testid="volunteer-card">
              <div className="admin-msg-head">
                <div>
                  <strong>{v.name}</strong> <a href={`mailto:${v.email}`}>{v.email}</a>
                </div>
                <div className="admin-msg-right">
                  <span className="admin-post-meta">{new Date(v.created_at).toLocaleString()}</span>
                  <button className="admin-icon-btn danger" onClick={() => handleDeleteVolunteer(v.id)} data-testid="volunteer-delete-btn"><Trash2 size={15} /></button>
                </div>
              </div>
              <p><strong>DOB:</strong> {v.dob} · <strong>Phone:</strong> {v.phone}</p>
              <p><strong>Areas:</strong> {(v.areas || []).join(', ')}</p>
              <p><strong>Leagues:</strong> {(v.leagues || []).join(', ')}</p>
              {v.experience && v.experience.trim() && <p><strong>Experience:</strong> {v.experience}</p>}
            </div>
          ))}
        </div>
      )}

      {tab === 'results' && (
        <div className="admin-post-list">
          <form className="admin-form admin-notice-form" onSubmit={handleAddResultsLink}>
            <h3 className="admin-settings-title"><BarChart3 size={17} /> Add a Results &amp; Stats Link</h3>
            <p className="admin-sub">Links appear on the public Results &amp; Stats page (/results), grouped by league, division and season.</p>
            <div className="admin-form-row">
              <div>
                <label>League</label>
                <select
                  className="admin-league-select"
                  value={resultsForm.league}
                  onChange={(e) => {
                    const lg = STATS_LEAGUES.find((x) => x.key === e.target.value);
                    setResultsForm({ ...resultsForm, league: e.target.value, division: lg?.divisions[0] || '' });
                  }}
                  data-testid="results-form-league"
                >
                  {STATS_LEAGUES.map((l) => <option key={l.key} value={l.key}>{l.label}</option>)}
                </select>
              </div>
              <div>
                <label>Division</label>
                <select
                  className="admin-league-select"
                  value={resultsForm.division}
                  onChange={(e) => setResultsForm({ ...resultsForm, division: e.target.value })}
                  disabled={!STATS_LEAGUES.find((x) => x.key === resultsForm.league)?.divisions.length}
                  data-testid="results-form-division"
                >
                  {(STATS_LEAGUES.find((x) => x.key === resultsForm.league)?.divisions || []).map((d) => <option key={d} value={d}>{d}</option>)}
                  {!STATS_LEAGUES.find((x) => x.key === resultsForm.league)?.divisions.length && <option value="">N/A</option>}
                </select>
              </div>
              <div>
                <label>Season</label>
                <Input value={resultsForm.season} onChange={(e) => setResultsForm({ ...resultsForm, season: e.target.value })} placeholder="2026-27" data-testid="results-form-season" />
              </div>
            </div>
            <label>Link Title (shown next to the season)</label>
            <Input value={resultsForm.label} onChange={(e) => setResultsForm({ ...resultsForm, label: e.target.value })} placeholder="E.g. Deloitte U19 Boys League 2026" data-testid="results-form-label" />
            <label>Link URL</label>
            <Input value={resultsForm.url} onChange={(e) => setResultsForm({ ...resultsForm, url: e.target.value })} placeholder="https://..." data-testid="results-form-url" />
            <div className="admin-form-actions">
              <Button type="submit" disabled={saving} className="btn-submit" data-testid="results-form-add-btn">{saving ? 'Adding...' : 'Add Link'}</Button>
            </div>
          </form>

          {resultsLinks.length === 0 && <p className="empty-state">No results links yet. The public page shows "Coming soon" for each league.</p>}
          {resultsLinks.map((l) => (
            <div key={l.id} className="admin-post-row" data-testid="results-link-row">
              <div className="admin-post-info">
                <span className="admin-post-title">{[l.league, l.division].filter(Boolean).join(' ')} · {l.season} — {l.label || 'Results & Stats'}</span>
                <span className="admin-post-meta"><a href={l.url} target="_blank" rel="noreferrer">{l.url} <ExternalLink size={11} style={{ display: 'inline' }} /></a></span>
              </div>
              <div className="admin-post-actions">
                <button className="admin-icon-btn danger" onClick={() => handleDeleteResultsLink(l.id)} data-testid="results-link-delete-btn"><Trash2 size={15} /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === 'letters' && (
        <div className="admin-post-list">
          {letterRequests.length === 0 && <p className="empty-state">No letter requests yet.</p>}
          {letterRequests.map((r) => (
            <div key={r.id} className="admin-msg-card">
              <div className="admin-msg-head">
                <div>
                  <strong>{r.requestor_name}</strong> <a href={`mailto:${r.requestor_email}`}>{r.requestor_email}</a>
                  <span className="admin-msg-subject"> — {r.purpose}</span>
                  <span className={`reg-status ${r.email_status === 'sent' ? 'ok' : r.email_status === 'failed' ? 'bad' : ''}`}>
                    Email: {r.email_status || 'pending'}
                  </span>
                </div>
                <div className="admin-msg-right">
                  <span className="admin-post-meta">{new Date(r.created_at).toLocaleString()}</span>
                  <button className="admin-icon-btn danger" onClick={() => handleDeleteLetter(r.id)}><Trash2 size={15} /></button>
                </div>
              </div>
              <p><strong>Organization:</strong> {r.organization}</p>
              <p><strong>Addressed to:</strong> {r.recipient_person}</p>
              <p><strong>Event:</strong> {r.event_details}</p>
            </div>
          ))}
        </div>
      )}

      {tab === 'messages' && (
        <div className="admin-post-list">
          {messages.length === 0 && <p className="empty-state">No contact messages yet.</p>}
          {messages.map((m) => (
            <div key={m.id} className="admin-msg-card">
              <div className="admin-msg-head">
                <div>
                  <strong>{m.name}</strong> <a href={`mailto:${m.email}`}>{m.email}</a>
                  {m.subject && <span className="admin-msg-subject"> — {m.subject}</span>}
                </div>
                <div className="admin-msg-right">
                  <span className="admin-post-meta">{new Date(m.created_at).toLocaleString()}</span>
                  <button className="admin-icon-btn danger" onClick={() => handleDeleteMsg(m.id)}><Trash2 size={15} /></button>
                </div>
              </div>
              <p>{m.message}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
